import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Search, UserPlus, Users, FileSpreadsheet, Star, Archive, RotateCcw, Trash2, AlertTriangle, FileText } from "lucide-react";
import ImportExcelModal from "@/components/ImportExcelModal";
import EntityFormDialog from "@/components/EntityFormDialog";
import StaffEvalReportModal from "@/components/StaffEvalReportModal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

function Pill({ value }) {
  const score = value || 0;
  const color = score >= 7.5 ? "verde" : score >= 5 ? "giallo" : "rosso";
  const m = {
    verde: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    giallo: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    rosso: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
  };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${m[color]}`}>{score.toFixed(1)}</span>;
}

export default function Staff() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreate = ["super_admin", "resident_manager", "housekeeping_manager", "front_office_manager"].includes(user?.role);
  const [staff, setStaff] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fHotel, setFHotel] = useState("");
  const [fDept, setFDept] = useState("");
  const [fRuolo, setFRuolo] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const canImport = ["super_admin", "resident_manager", "housekeeping_manager", "front_office_manager"].includes(user?.role);

  const isAdmin = user?.role === "super_admin";
  const canGenerateReport = ["super_admin", "resident_manager"].includes(user?.role);
  const [evalReportOpen, setEvalReportOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // staff doc (soft delete / archive)
  const [confirmHardDelete, setConfirmHardDelete] = useState(null); // staff doc (hard delete)
  const [hardDeleteCognome, setHardDeleteCognome] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        api.get(`/staff?${showArchived ? "archived=true" : "attivo=true"}`),
        api.get("/hotels"),
      ]);
      setStaff(s.data || []);
      setHotels(h.data || []);
      // load departments for all hotels
      const allDepts = [];
      for (const hotel of h.data || []) {
        try {
          const d = await api.get(`/departments?hotel_id=${hotel.id}`);
          allDepts.push(...(d.data || []));
        } catch {}
      }
      setDepts(allDepts);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showArchived]);

  const doDelete = async () => {
    if (!confirmDelete) return;
    setBusyId(confirmDelete.id);
    try {
      await api.delete(`/staff/${confirmDelete.id}`);
      toast.success(`${confirmDelete.nome} ${confirmDelete.cognome} archiviato`);
      setConfirmDelete(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };
  const doRestore = async (s) => {
    setBusyId(s.id);
    try {
      await api.post(`/staff/${s.id}/restore`);
      toast.success(`${s.nome} ${s.cognome} ripristinato`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const doHardDelete = async () => {
    if (!confirmHardDelete) return;
    setBusyId(confirmHardDelete.id);
    try {
      const { data } = await api.delete(`/staff/${confirmHardDelete.id}/hard`);
      toast.success(`${confirmHardDelete.nome} ${confirmHardDelete.cognome} eliminato definitivamente · ${data.deleted_reviews || 0} valutazioni · ${data.deleted_stars || 0} stelle rimosse`);
      setConfirmHardDelete(null);
      setHardDeleteCognome("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const filtered = useMemo(() => {
    return staff.filter((s) => {
      if (fHotel && s.hotel_id !== fHotel) return false;
      if (fDept && s.department_id !== fDept) return false;
      if (fRuolo && s.ruolo !== fRuolo) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(`${s.nome} ${s.cognome} ${s.ruolo}`).toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [staff, fHotel, fDept, fRuolo, search]);

  const ruoliUnici = useMemo(() => Array.from(new Set(staff.map((s) => s.ruolo))).sort(), [staff]);

  return (
    <div className="space-y-6 ih-anim-in" data-testid="staff-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Staff</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light">Anagrafica <span className="font-medium">dipendenti</span></h1>
          <p className="text-sm text-muted-foreground mt-1">{filtered.length} su {staff.length} totali</p>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            {canGenerateReport && !showArchived && (
              <button
                onClick={() => setEvalReportOpen(true)}
                data-testid="staff-eval-report-btn"
                className="px-4 py-2 rounded-full border border-lavender-400 text-lavender-700 dark:text-lavender-300 dark:border-lavender-500/60 hover:bg-lavender-50 dark:hover:bg-lavender-950/30 text-sm font-medium flex items-center gap-2"
              >
                <FileText className="h-4 w-4" /> Report Valutazioni
              </button>
            )}
            {isAdmin && (
              <button onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived" className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 ${showArchived ? "bg-rose-500 text-white" : "border border-border hover:bg-accent"}`}>
                <Archive className="h-4 w-4" /> {showArchived ? "Mostra attivi" : "Archiviati"}
              </button>
            )}
            {canImport && !showArchived && (
              <button onClick={() => setImportOpen(true)} data-testid="import-staff-btn" className="px-4 py-2 rounded-full border border-border text-sm font-medium flex items-center gap-2 hover:bg-accent">
                <FileSpreadsheet className="h-4 w-4" /> Importa da Excel
              </button>
            )}
            {!showArchived && (
              <button onClick={() => setShowAdd(true)} data-testid="staff-add-btn" className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:opacity-90">
                <UserPlus className="h-4 w-4" /> Nuovo dipendente
              </button>
            )}
          </div>
        )}
      </div>
      {importOpen && <ImportExcelModal entity="staff" onClose={() => setImportOpen(false)} onComplete={load} />}
      {evalReportOpen && (
        <StaffEvalReportModal
          open={evalReportOpen}
          onClose={() => setEvalReportOpen(false)}
          defaultHotelId={fHotel || undefined}
        />
      )}

      {/* Filtri */}
      <div className="ih-glass rounded-2xl p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="staff-search" type="text" placeholder="Cerca per nome, cognome, ruolo…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background/60 text-sm" />
        </div>
        <select data-testid="filter-hotel" value={fHotel} onChange={(e) => setFHotel(e.target.value)} className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
          <option value="">Tutti gli hotel</option>
          {hotels.map((h) => <option key={h.id} value={h.id}>{h.nome}</option>)}
        </select>
        <select data-testid="filter-dept" value={fDept} onChange={(e) => setFDept(e.target.value)} className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
          <option value="">Tutti i reparti</option>
          {depts.filter((d) => !fHotel || d.hotel_id === fHotel).map((d) => <option key={d.id} value={d.id}>{d.nome} ({hotels.find((h) => h.id === d.hotel_id)?.citta})</option>)}
        </select>
        <select data-testid="filter-ruolo" value={fRuolo} onChange={(e) => setFRuolo(e.target.value)} className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
          <option value="">Tutti i ruoli</option>
          {ruoliUnici.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((s) => {
            const h = hotels.find((x) => x.id === s.hotel_id);
            const d = depts.find((x) => x.id === s.department_id);
            return (
              <div key={s.id} data-testid={`staff-card-${s.id}`} className={`ih-glass rounded-2xl p-5 ih-card-hover relative ${showArchived ? "opacity-80" : ""}`}>
                <div onClick={() => navigate(`/staff/${s.id}`)} className="cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-lavender-200 dark:bg-lavender-900/40 flex items-center justify-center font-bold text-sm">{s.nome[0]}{s.cognome[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate flex items-center gap-1.5">
                      {s.nome} {s.cognome}
                      {Number.isFinite(s.eta) && <span className="text-muted-foreground font-normal"> · {s.eta} anni</span>}
                      {s.stars_count > 0 && (
                        <span title={`${s.stars_count} stelle obiettivi`} className="inline-flex items-center gap-0.5 text-[11px] font-medium text-lavender-600 dark:text-lavender-400 bg-lavender-50 dark:bg-lavender-900/20 px-1.5 py-0.5 rounded-full" data-testid={`stars-badge-${s.id}`}>
                          <Star className="h-3 w-3 fill-lavender-500 text-lavender-500" />
                          {s.stars_count}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{s.ruolo}</div>
                  </div>
                  {s.ultima_valutazione && <Pill value={s.ultima_valutazione.punteggio_medio} />}
                </div>
                <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground">
                  <Users className="h-3 w-3" /> <span className="truncate">{d?.nome || "—"}</span>
                  <span>·</span>
                  <span className="truncate">{h?.nome || "—"}</span>
                </div>
                </div>
                {isAdmin && (
                  <div className="mt-3 pt-3 border-t border-border/40 flex items-center justify-between">
                    {showArchived ? (
                      <>
                        <span className="text-[10px] text-rose-500 inline-flex items-center gap-1"><Archive className="h-3 w-3" /> Archiviato</span>
                        <div className="flex items-center gap-3">
                          <button onClick={(e) => { e.stopPropagation(); doRestore(s); }} disabled={busyId === s.id} data-testid={`restore-${s.id}`} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1">
                            {busyId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Ripristina
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); setHardDeleteCognome(""); setConfirmHardDelete(s); }} disabled={busyId === s.id} data-testid={`hard-delete-${s.id}`} className="text-xs font-medium text-rose-600 hover:text-rose-700 inline-flex items-center gap-1">
                            <Trash2 className="h-3 w-3" /> Elimina definitivamente
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="text-[10px] text-emerald-600 inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Attivo</span>
                        <button onClick={(e) => { e.stopPropagation(); setConfirmDelete(s); }} disabled={busyId === s.id} data-testid={`delete-${s.id}`} className="text-xs font-medium text-rose-500 hover:text-rose-600 inline-flex items-center gap-1">
                          <Trash2 className="h-3 w-3" /> Elimina
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && <div className="col-span-full ih-empty-state"><div className="ih-empty-state-icon"><svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg></div><h3 className="text-lg font-semibold mb-1">Nessun dipendente registrato</h3><p className="text-sm text-muted-foreground max-w-md">Aggiungi i tuoi dipendenti manualmente o importa l'elenco completo da un file Excel.</p></div>}
        </div>
      )}

      {showAdd && <AddStaffModal open={showAdd} hotels={hotels} depts={depts} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}

      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-delete-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Archive className="h-4 w-4 text-rose-500" /> Archivia dipendente
            </DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare <strong>{confirmDelete?.nome} {confirmDelete?.cognome}</strong>? Verrà archiviato insieme alle sue valutazioni e stelle. <span className="text-emerald-600">L'azione è reversibile dal super admin.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annulla</Button>
            <Button onClick={doDelete} disabled={busyId === confirmDelete?.id} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="confirm-delete-btn">
              {busyId === confirmDelete?.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
              Archivia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmHardDelete} onOpenChange={(o) => { if (!o) { setConfirmHardDelete(null); setHardDeleteCognome(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-hard-delete-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2 text-rose-600">
              <AlertTriangle className="h-5 w-5" /> Eliminazione definitiva irreversibile
            </DialogTitle>
            <DialogDescription className="space-y-3">
              <span className="block">
                Stai per eliminare definitivamente <strong className="text-foreground">{confirmHardDelete?.nome} {confirmHardDelete?.cognome}</strong> e tutti i dati collegati (valutazioni, stelle, storico). <strong className="text-rose-600">L'operazione NON è reversibile.</strong>
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <label className="text-xs text-muted-foreground">
              Per confermare, digita esattamente il cognome del dipendente: <strong className="text-foreground">{confirmHardDelete?.cognome}</strong>
            </label>
            <Input
              value={hardDeleteCognome}
              onChange={(e) => setHardDeleteCognome(e.target.value)}
              placeholder={confirmHardDelete?.cognome || ""}
              autoFocus
              data-testid="hard-delete-cognome-input"
              className="font-mono"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmHardDelete(null); setHardDeleteCognome(""); }}>Annulla</Button>
            <Button
              onClick={doHardDelete}
              disabled={busyId === confirmHardDelete?.id || hardDeleteCognome !== (confirmHardDelete?.cognome || "")}
              className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="confirm-hard-delete-btn"
            >
              {busyId === confirmHardDelete?.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddStaffModal({ open, hotels, depts, onClose, onSaved }) {
  const [form, setForm] = useState({ nome: "", cognome: "", eta: "", ruolo: "", hotel_id: hotels[0]?.id || "", department_id: "", data_assunzione: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.cognome || !form.ruolo || !form.hotel_id) {
      toast.error("Compila i campi obbligatori");
      return;
    }
    // Validate eta if provided
    let etaParsed = null;
    if (form.eta !== "" && form.eta !== null && form.eta !== undefined) {
      const n = parseInt(form.eta, 10);
      if (Number.isNaN(n) || n < 0 || n > 99) {
        toast.error("Età non valida (0-99)");
        return;
      }
      etaParsed = n;
    }
    setSaving(true);
    try {
      await api.post("/staff", { ...form, eta: etaParsed });
      toast.success("Dipendente aggiunto");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <EntityFormDialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title="Nuovo dipendente"
      description="Aggiungi un nuovo dipendente all'anagrafica."
      size="md"
      onSubmit={submit}
      submitLabel="Salva dipendente"
      saving={saving}
      testId="staff-add-modal"
      submitTestId="add-submit"
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Nome</Label>
          <Input data-testid="add-nome" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Cognome</Label>
          <Input data-testid="add-cognome" required value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Età <span className="text-muted-foreground text-xs">(opz., 0-99)</span></Label>
        <Input
          data-testid="add-eta"
          type="number"
          min={0}
          max={99}
          placeholder="Es. 35"
          value={form.eta}
          onChange={(e) => setForm({ ...form, eta: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label>Ruolo</Label>
        <Input data-testid="add-ruolo" required placeholder="Es. Receptionist, Cameriere ai piani…" value={form.ruolo} onChange={(e) => setForm({ ...form, ruolo: e.target.value })} />
      </div>
      <div className="space-y-2">
        <Label>Hotel</Label>
        <Select value={form.hotel_id} onValueChange={(v) => setForm({ ...form, hotel_id: v, department_id: "" })}>
          <SelectTrigger data-testid="add-hotel"><SelectValue placeholder="Seleziona hotel" /></SelectTrigger>
          <SelectContent>
            {hotels.map((h) => <SelectItem key={h.id} value={h.id}>{h.nome}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Reparto <span className="text-muted-foreground text-xs">(opz.)</span></Label>
          <Select value={form.department_id || "__none__"} onValueChange={(v) => setForm({ ...form, department_id: v === "__none__" ? "" : v })}>
            <SelectTrigger data-testid="add-dept"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Nessuno —</SelectItem>
              {depts.filter((d) => d.hotel_id === form.hotel_id).map((d) => <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Data assunzione <span className="text-muted-foreground text-xs">(opz.)</span></Label>
          <Input data-testid="add-data" type="date" value={form.data_assunzione} onChange={(e) => setForm({ ...form, data_assunzione: e.target.value })} />
        </div>
      </div>
    </EntityFormDialog>
  );
}
